import {
  ApiPaginationDefaultParams,
  AqForecastModel,
  AqForecastResponse,
  DataGranularity,
  EmptyResponse,
  ForecastComparisonResponse,
  ForecastMeasure,
  ForecastModel,
  HistoryMeasureResult,
  HistoryMetric,
  PaginatedResult,
  StationSource,
  TypedApiRequestParams,
  WeatherForecast,
  WeatherMapItem,
  WeatherStationFromDetail,
  WeatherStationFromList,
  WeatherWarningListResponse,
  StationRecords,
  WarningAreasResponse,
  WarningsSummaryResponse,
} from '../types';
import {ForecastModelConfig} from '../../utils/Forecast/types';

type StationFilter = {
  name?: string;
  source?: StationSource[];
  is_airport?: boolean;
  location?: string;
  max_distance?: number;
  with_measures?: boolean;
  favorite?: boolean;
  place_id?: string;
} & ApiPaginationDefaultParams;

const stationList = (
  filter: StationFilter,
): TypedApiRequestParams<PaginatedResult<WeatherStationFromList[]>> => ({
  url: 'stations/',
  method: 'GET',
  params: {with_measures: true, ...filter},
  authRequired: false,
});

const myStationList = (
  otherStationIds?: number[],
): TypedApiRequestParams<WeatherStationFromList[]> => ({
  url: 'my-stations/',
  method: 'GET',
  params: {other_ids: otherStationIds || undefined},
  authRequired: true,
});

export type StationDetailOptions = {refresh?: boolean};

const stationDetail = (
  stationId: number,
  options?: StationDetailOptions,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/`,
  method: 'GET',
  params: options?.refresh ? {refresh: 1} : undefined,
  authRequired: false,
});

const addFavoriteStation = (
  stationId: number,
): TypedApiRequestParams<WeatherStationFromList> => ({
  url: `stations/${stationId}/add-favorite/`,
  method: 'POST',
  authRequired: true,
});

const removeFavoriteStation = (
  stationId: number,
): TypedApiRequestParams<EmptyResponse> => ({
  url: `stations/${stationId}/remove-favorite/`,
  method: 'POST',
  authRequired: true,
});

const setShareStatus = (
  stationId: number,
  shared: boolean,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/share-status/`,
  method: 'POST',
  params: {shared},
  authRequired: true,
});

const renameLinkedStation = (
  stationId: number,
  name: string,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/rename/`,
  method: 'POST',
  params: {name},
  authRequired: true,
});

const hideLinkedStation = (
  stationId: number,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/hide/`,
  method: 'POST',
  authRequired: true,
});

const unhideLinkedStation = (
  stationId: number,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/unhide/`,
  method: 'POST',
  authRequired: true,
});

const renameSensors = (
  stationId: number,
  sensorNames: Record<string, string>,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/rename-sensors/`,
  method: 'POST',
  params: {sensor_names: sensorNames},
  authRequired: true,
});

const updateSensorConfig = (
  stationId: number,
  config: {
    sensor_names?: Record<string, string>;
    sensor_order?: Record<string, string[]>;
    sensor_hidden?: Record<string, string[]>;
  },
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/sensor-config/`,
  method: 'POST',
  params: config,
  authRequired: true,
});

const setRainCheck = (
  stationId: number,
  value: boolean,
): TypedApiRequestParams<WeatherStationFromDetail> => ({
  url: `stations/${stationId}/set-raincheck/`,
  method: 'POST',
  params: {value},
  authRequired: true,
});

const updateFavoriteStation = (
  stationId: number,
  name: string,
): TypedApiRequestParams<WeatherStationFromList> => ({
  url: `stations/${stationId}/update-favorite/`,
  method: 'POST',
  params: {name},
  authRequired: true,
});

type StationMapFilter = {
  source?: StationSource[];
  metric?: 'T' | 'RR_1H' | 'FF' | 'FXY' | 'SLP';
};

const weatherMapMarkers = (
  bbox: {
    latNe: number;
    lonNe: number;
    latSw: number;
    lonSw: number;
  },
  zoom: number,
  filter = {} as StationMapFilter,
): TypedApiRequestParams<WeatherMapItem[]> => ({
  url: 'map-cluster/',
  method: 'GET',
  params: {
    ...filter,
    in_bbox: `${bbox.lonSw},${bbox.latSw},${bbox.lonNe},${bbox.latNe}`,
    zoom,
  },
  authRequired: false,
});

type HistorySeries = {
  station: number;
  metrics: HistoryMetric[];
  scale: DataGranularity;
  start: number;
  end: number;
};

const historyMeasures = (
  series: HistorySeries[],
): TypedApiRequestParams<HistoryMeasureResult[]> => ({
  url: 'measures/',
  method: 'POST',
  params: {series: series.map((serie) => ({...serie, sharp: true}))},
  authRequired: false,
});

// Export report returns a URL string
const exportReport = (
  station: number,
): TypedApiRequestParams<{url: string}> => ({
  url: 'measures/report/export',
  method: 'GET',
  params: {station},
  authRequired: true,
});

/*
Station Forecast APIs
*/
const stationWeatherForecastModelList = (
  stationId: number,
): TypedApiRequestParams<ForecastModelConfig[]> => ({
  url: `stations/${stationId}/forecast_models/`,
  method: 'GET',
  authRequired: false,
});

const stationWeatherForecast = (
  stationId: number,
  forecastModel?: ForecastModel,
  meshSize?: string | null,
): TypedApiRequestParams<WeatherForecast> => ({
  url: `stations/${stationId}/forecasts/`,
  method: 'GET',
  params: {forecast_model: forecastModel, mesh_size: meshSize},
  authRequired: false,
});

const stationWeatherForecastCompare = (
  stationId: number,
  measures?: ForecastMeasure[],
  forecastModels?: ForecastModel[],
): TypedApiRequestParams<ForecastComparisonResponse> => ({
  url: `stations/${stationId}/forecast-comparisons/`,
  method: 'GET',
  params: {measures, forecast_models: forecastModels},
  authRequired: false,
});

const locationWeatherForecastModelList = (
  lat: number,
  lon: number,
): TypedApiRequestParams<ForecastModelConfig[]> => ({
  url: 'forecast/point/forecast-models/',
  method: 'GET',
  params: {lat, lon},
  authRequired: false,
});

type LocationWeatherForecastParams = {
  lat: number;
  lon: number;
  timezone: string;
  altitude?: number;
  forecastModel?: ForecastModel;
  meshSize?: string | null;
};

const locationWeatherForecast = ({
  lat,
  lon,
  altitude,
  timezone,
  forecastModel,
  meshSize,
}: LocationWeatherForecastParams): TypedApiRequestParams<WeatherForecast> => ({
  url: 'forecast/point/forecasts/',
  method: 'GET',
  params: {
    lat,
    lon,
    forecast_model: forecastModel,
    mesh_size: meshSize,
    altitude,
    timezone,
  },
  authRequired: false,
});

type LocationWeatherForecastCompareParams = {
  lat: number;
  lon: number;
  timezone: string;
  altitude?: number;
  measures?: ForecastMeasure[];
  forecastModels?: ForecastModel[];
};

const locationWeatherForecastCompare = ({
  lat,
  lon,
  altitude,
  timezone,
  measures,
  forecastModels,
}: LocationWeatherForecastCompareParams): TypedApiRequestParams<ForecastComparisonResponse> => ({
  url: 'forecast/point/forecast-comparison/',
  method: 'GET',
  params: {
    lat,
    lon,
    altitude,
    timezone,
    measures,
    forecast_models: forecastModels,
  },
  authRequired: false,
});

type LocationWeatherForecastWarningsParams = {
  lat: number;
  lon: number;
};

const locationWeatherWarnings = ({
  lat,
  lon,
}: LocationWeatherForecastWarningsParams): TypedApiRequestParams<WeatherWarningListResponse> => ({
  url: 'warnings/point/weather-warnings/',
  method: 'GET',
  params: {
    lat,
    lon,
  },
  authRequired: false,
});

/** A finite `{lat, lon}` pair, or undefined: half a point is no point. */
const pointParams = (
  lat: number | null | undefined,
  lon: number | null | undefined,
): {lat: number; lon: number} | undefined =>
  typeof lat === 'number' &&
  typeof lon === 'number' &&
  Number.isFinite(lat) &&
  Number.isFinite(lon)
    ? {lat, lon}
    : undefined;

type WarningAreasParams = {
  id: number;
  /** The place on screen; marks its area `own`. Optional. */
  lat?: number | null;
  lon?: number | null;
};

/**
 * Every area one warning covers, as GeoJSON, with the one containing the
 * point marked (Phase B). Open to any app user, signed in or not.
 */
const warningAreas = ({
  id,
  lat,
  lon,
}: WarningAreasParams): TypedApiRequestParams<WarningAreasResponse> => ({
  url: `warnings/${id}/areas/`,
  method: 'GET',
  params: pointParams(lat, lon),
  authRequired: false,
});

/** Points one `warnings/summary/` request may carry (the backend's limit). */
export const WARNINGS_SUMMARY_MAX_POINTS = 100;

export type WarningsSummaryPoint = {lat: number; lon: number};

/**
 * The worst current warning at each point, in order. One request, so at most
 * `WARNINGS_SUMMARY_MAX_POINTS` points: use `fetchWarningsSummary` for more.
 */
const warningsSummary = (
  points: readonly WarningsSummaryPoint[],
): TypedApiRequestParams<WarningsSummaryResponse> => {
  if (points.length > WARNINGS_SUMMARY_MAX_POINTS) {
    throw new RangeError(
      `warningsSummary takes at most ${WARNINGS_SUMMARY_MAX_POINTS} points, got ${points.length}`,
    );
  }
  return {
    url: 'warnings/summary/',
    method: 'GET',
    // Serialised as repeated `p=lat,lon` (`apiClient`: arrayFormat repeat).
    params: {p: points.map(({lat, lon}) => `${lat},${lon}`)},
    authRequired: false,
  };
};

type SummaryFetch = (
  request: TypedApiRequestParams<WarningsSummaryResponse>,
  options?: {silent?: boolean; ignore404?: boolean},
) => Promise<WarningsSummaryResponse>;

/**
 * `warningsSummary` for any number of points: split into requests of at most
 * 100, sent together, and re-joined in the order of `points`. A point that
 * cannot be sent (a non-finite coordinate) answers null without a request.
 *
 * `computed_at` is the OLDEST of the requests', so a caller judging freshness
 * never takes a stale chunk for a fresh one. Rejects if any request fails, or
 * answers a different number of items than it was asked points: an item out of
 * line would badge the wrong station, which is worse than no badge.
 */
export const fetchWarningsSummary = async (
  fetch: SummaryFetch,
  points: readonly WarningsSummaryPoint[],
  options?: {silent?: boolean; ignore404?: boolean},
): Promise<WarningsSummaryResponse> => {
  const sendable: {index: number; point: WarningsSummaryPoint}[] = [];
  points.forEach((p, index) => {
    const point = pointParams(p?.lat, p?.lon);
    if (point) {
      sendable.push({index, point});
    }
  });
  const chunks: (typeof sendable)[] = [];
  for (let i = 0; i < sendable.length; i += WARNINGS_SUMMARY_MAX_POINTS) {
    chunks.push(sendable.slice(i, i + WARNINGS_SUMMARY_MAX_POINTS));
  }
  const responses = await Promise.all(
    chunks.map((chunk) =>
      fetch(warningsSummary(chunk.map(({point}) => point)), options),
    ),
  );

  const items: WarningsSummaryResponse['items'] = points.map(() => null);
  let computedAt: number | null = null;
  responses.forEach((response, c) => {
    const chunk = chunks[c];
    if (
      !Array.isArray(response?.items) ||
      response.items.length !== chunk.length
    ) {
      throw new Error(
        `warnings/summary answered ${
          Array.isArray(response?.items) ? response.items.length : 'no'
        } items for ${chunk.length} points`,
      );
    }
    chunk.forEach(({index}, i) => {
      items[index] = response.items[i] ?? null;
    });
    if (
      typeof response.computed_at === 'number' &&
      Number.isFinite(response.computed_at) &&
      (computedAt === null || response.computed_at < computedAt)
    ) {
      computedAt = response.computed_at;
    }
  });
  return {items, computed_at: computedAt};
};

/*
Station AQ Forecast APIs
*/
const stationAqForecastModelList = (
  stationId: number,
): TypedApiRequestParams<AqForecastModel[]> => ({
  url: `stations/${stationId}/air-quality-forecast-models/`,
  method: 'GET',
  authRequired: false,
});

const stationAqForecast = (
  stationId: number,
  forecastModel?: string,
  meshSize?: string,
): TypedApiRequestParams<AqForecastResponse> => ({
  url: `stations/${stationId}/air-quality-forecasts/`,
  method: 'GET',
  params: {forecast_model: forecastModel, mesh_size: meshSize},
  authRequired: false,
});

/*
Station all-time records & climatology
*/
const stationRecords = (
  stationId: number,
  params?: {month?: number; day?: number; sections?: string},
): TypedApiRequestParams<StationRecords> => ({
  url: `stations/${stationId}/records/`,
  method: 'GET',
  params,
  authRequired: false,
});

/*
Location AQ Forecast APIs
*/
const locationAqForecastModelList = (
  lat: number,
  lon: number,
): TypedApiRequestParams<AqForecastModel[]> => ({
  url: 'forecast/point/air-quality-forecast-models/',
  method: 'GET',
  params: {lat, lon},
  authRequired: false,
});

type LocationAqForecastParams = {
  lat: number;
  lon: number;
  timezone: string;
  altitude?: number;
  forecastModel?: string;
  meshSize?: string;
};

const locationAqForecast = ({
  lat,
  lon,
  altitude,
  timezone,
  forecastModel,
  meshSize,
}: LocationAqForecastParams): TypedApiRequestParams<AqForecastResponse> => ({
  url: 'forecast/point/air-quality-forecasts/',
  method: 'GET',
  params: {
    lat,
    lon,
    forecast_model: forecastModel,
    mesh_size: meshSize,
    altitude,
    timezone,
  },
  authRequired: false,
});

export default {
  stationList,
  locationWeatherWarnings,
  warningAreas,
  warningsSummary,
  locationWeatherForecastCompare,
  stationDetail,
  addFavoriteStation,
  removeFavoriteStation,
  updateFavoriteStation,
  setShareStatus,
  myStationList,
  weatherMapMarkers,
  historyMeasures,
  stationWeatherForecastModelList,
  stationWeatherForecast,
  stationWeatherForecastCompare,
  exportReport,
  renameLinkedStation,
  renameSensors,
  setRainCheck,
  locationWeatherForecastModelList,
  locationWeatherForecast,
  hideLinkedStation,
  unhideLinkedStation,
  updateSensorConfig,
  stationAqForecastModelList,
  stationAqForecast,
  stationRecords,
  locationAqForecastModelList,
  locationAqForecast,
};
